import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { Lock } from "lucide-react";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { LanguageProvider } from "./context/LanguageContext";
import { ThemeProvider } from "./context/ThemeContext";
import { usePageView } from "./lib/analytics";
import Auth from "./pages/Auth";
import MobileLayout from "./components/MobileLayout";
import { InstallProvider } from "./context/InstallContext";
import { CanonicalOrigin } from "./components/CanonicalOrigin";
import { SessionAvailability } from "./components/SessionAvailability";
import Dashboard from "./pages/Dashboard";
import BonusCard from "./pages/BonusCard";
import Catalog from "./pages/Catalog";
import ProductDetail from "./pages/ProductDetail";
import News from "./pages/News";
import Appointment from "./pages/Appointment";
import Profile from "./pages/Profile";
import VirtualTryOn from "./pages/VirtualTryOn";
import BrandDetail from "./pages/BrandDetail";
import Legal from "./pages/Legal";
import InfoPage from "./pages/InfoPage";
import { useSeo } from "./lib/seo";
import { useAndroidBackButton } from "./hooks/useAndroidBackButton";
import Admin from "./pages/Admin";
import PublicShare from "./pages/PublicShare";
import AcademyRoot, { AcademyLessonRoot } from "./pages/Academy";
import AcademyFinalExam from "./pages/AcademyFinalExam";
import PublicProductPreview from "./pages/PublicProductPreview";
import "./App.css";
import "./index.css";

function Loader() {
  return (
    <div style={{ background: "#0A0A0A", height: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ fontFamily: "Italiana", fontSize: "36px", color: "#C5A059", letterSpacing: "0.06em" }}>Angiolini</div>
    </div>
  );
}

const CLIENT_ONLY = process.env.REACT_APP_CLIENT_ONLY === "true";

function ProtectedClientRoute({ children }) {
  const { loading } = useAuth();
  if (loading) return <Loader />;
  // Гостевой доступ: витрину (главная, каталог, карточки, бренды, новости,
  // контакты) смотрят без регистрации — требование Apple 5.1.1(v).
  // Персональные разделы закрыты отдельно через <RequireAuth>.
  return children;
}

/** Экран-приглашение вместо персонального раздела, пока гость не вошёл. */
function RequireAuth({ children, title, text }) {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  useSeo({ title: `${title} — ANGIOLINI`, noindex: true });
  if (loading) return <Loader />;
  if (user) return children;
  return (
    <div data-testid="guest-gate" style={{ minHeight: "100vh", background: "var(--ang-bg)",
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
      textAlign: "center", padding: "0 28px", gap: "14px" }}>
      <Lock size={26} color="var(--ang-gold)" />
      <div style={{ fontFamily: "Cormorant Garamond", fontSize: "26px", color: "var(--ang-text)" }}>{title}</div>
      <div style={{ fontFamily: "Manrope", fontSize: "13px", color: "var(--ang-text-soft)", lineHeight: 1.6, maxWidth: "320px" }}>{text}</div>
      <button data-testid="guest-gate-login" onClick={() => navigate("/auth")}
        style={{ marginTop: "8px", background: "var(--ang-gold)", color: "#1A1A1A", border: "none",
          borderRadius: "999px", padding: "14px 26px", fontFamily: "Manrope", fontSize: "12px",
          fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", cursor: "pointer" }}>
        Войти / Получить VIP-привилегии
      </button>
      <button data-testid="guest-gate-back" onClick={() => navigate("/app/catalog")}
        style={{ background: "transparent", border: "none", color: "var(--ang-text-faint)",
          fontFamily: "Manrope", fontSize: "12px", textDecoration: "underline", cursor: "pointer" }}>
        Продолжить просмотр каталога
      </button>
    </div>
  );
}

function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading) return <Loader />;
  if (user && ["admin", "manager"].includes(user.role)) return <Navigate to="/admin" replace />;
  // Гость тоже попадает сразу в витрину, а не на экран входа.
  return <Navigate to="/app/home" replace />;
}

function AnalyticsTracker() {
  usePageView();
  return null;
}

/** Ловим неожиданную JS-ошибку, чтобы клиент не смотрел в пустой экран. */
function AndroidBackButton() {
  useAndroidBackButton();
  return null;
}

class AppErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { failed: false }; }
  static getDerivedStateFromError() { return { failed: true }; }
  componentDidCatch(error, info) { console.error("App crashed:", error, info); }
  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <div style={{ minHeight: "100vh", background: "#0A0A0A", display: "flex",
                     flexDirection: "column", alignItems: "center",
                     justifyContent: "center", gap: 16, padding: 24, textAlign: "center" }}>
        <div style={{ fontFamily: "Italiana", fontSize: 34, color: "#C5A059" }}>Angiolini</div>
        <div style={{ fontFamily: "Manrope", fontSize: 14, color: "#c9c9c7", maxWidth: 320 }}>
          Страница не загрузилась до конца. Обновите её — обычно этого достаточно.
        </div>
        <button onClick={() => window.location.reload()}
          style={{ background: "linear-gradient(135deg,#C5A059,#D4B878)", color: "#0A0A0A",
                    border: "none", borderRadius: 8, padding: "12px 22px", fontWeight: 700,
                    fontFamily: "Manrope", fontSize: 13, cursor: "pointer" }}>
          Обновить страницу
        </button>
      </div>
    );
  }
}

function App() {
  return (
    <AppErrorBoundary>
    <CanonicalOrigin>
    <ThemeProvider>
    <LanguageProvider>
      <AuthProvider>
        <BrowserRouter>
          <SessionAvailability>
          <InstallProvider>
          <AnalyticsTracker />
          <AndroidBackButton />
          <Routes>
            <Route path="/" element={<RootRedirect />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/legal" element={<Legal />} />
            <Route path="/info/:slug" element={<InfoPage />} />
            {/* Public share routes — no auth required, opened via WhatsApp */}
            <Route path="/share/:token" element={<PublicShare />} />
            <Route path="/preview/product/:id" element={<PublicProductPreview />} />
            <Route path="/app" element={<ProtectedClientRoute><MobileLayout /></ProtectedClientRoute>}>
              <Route index element={<Navigate to="home" replace />} />
              <Route path="home" element={<Dashboard />} />
              <Route path="catalog" element={<Catalog />} />
              <Route path="category/:categorySlug" element={<Catalog />} />
              <Route path="catalog/:id" element={<ProductDetail />} />
              <Route path="bonus" element={<RequireAuth title="VIP-карта" text="Личная бонусная карта, кэшбэк и история покупок доступны участникам VIP-клуба. Регистрация занимает минуту — по номеру телефона.">
                <BonusCard />
              </RequireAuth>} />
              <Route path="news" element={<News />} />
              <Route path="appointment" element={<Appointment />} />
              <Route path="tryon" element={<RequireAuth title="AI-примерка" text="Примерьте вещь на своё фото. Нужен аккаунт: так результаты сохраняются только в вашем личном кабинете и вы можете удалить их в любой момент.">
                <VirtualTryOn />
              </RequireAuth>} />
              <Route path="brand/:slug" element={<BrandDetail />} />
              <Route path="profile" element={<RequireAuth title="Личный кабинет" text="Профиль, уровень VIP, бонусы и сохранённые примерки появятся после входа по номеру телефона.">
                <Profile />
              </RequireAuth>} />
            </Route>
            {/* В iOS-сборке (REACT_APP_CLIENT_ONLY=true) кабинеты сотрудников
                не собираются: Apple одобряет клиентское приложение, а CRM/ERP
                и Академия остаются только в веб-версии. */}
            {!CLIENT_ONLY && <Route path="/admin/*" element={<Admin />} />}
            {!CLIENT_ONLY && <Route path="/academy" element={<AcademyRoot />} />}
            {!CLIENT_ONLY && <Route path="/academy/final-exam" element={<AcademyFinalExam />} />}
            {!CLIENT_ONLY && <Route path="/academy/lesson/:lessonId" element={<AcademyLessonRoot />} />}
            {/* Любой неизвестный путь ведём в приложение, а не в пустоту:
                раньше опечатка/устаревшая ссылка давала чёрный экран. */}
            <Route path="*" element={<RootRedirect />} />
          </Routes>
          </InstallProvider>
          </SessionAvailability>
        </BrowserRouter>
      </AuthProvider>
    </LanguageProvider>
    </ThemeProvider>
    </CanonicalOrigin>
    </AppErrorBoundary>
  );
}

export default App;
